'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PackagingElement, PackagingGroup } from '@/lib/mockData';

interface PackagingContextValue {
  elements: PackagingElement[];
  groups: PackagingGroup[];
  loading: boolean;
  setElements: React.Dispatch<React.SetStateAction<PackagingElement[]>>;
  setGroups: React.Dispatch<React.SetStateAction<PackagingGroup[]>>;
  addElement: (el: Omit<PackagingElement, 'id'>) => Promise<void>;
  updateElement: (id: string, el: Partial<Omit<PackagingElement, 'id'>>) => Promise<void>;
  deleteElement: (id: string) => Promise<void>;
  addGroup: (g: Omit<PackagingGroup, 'id'>) => Promise<void>;
  updateGroup: (id: string, g: Partial<Omit<PackagingGroup, 'id'>>) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
}

const PackagingContext = createContext<PackagingContextValue | null>(null);

function rowToElement(r: any): PackagingElement {
  return {
    id: r.id,
    name: r.name,
    unit: r.unit,
    costPerUnit: Number(r.cost_per_unit),
    stockQty: Number(r.stock_qty),
    reorderLevel: Number(r.reorder_level),
    category: r.category,
    supplier: r.supplier ?? undefined,
    notes: r.notes ?? undefined,
    imageUrl: r.image_url ?? undefined,
  };
}

function rowToGroup(r: any): PackagingGroup {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? undefined,
    items: r.items ?? [],
    imageUrl: r.image_url ?? undefined,
  };
}

export function PackagingProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const supabase = createClient();
  const [elements, setElements] = useState<PackagingElement[]>([]);
  const [groups, setGroups] = useState<PackagingGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [elResult, grpResult] = await Promise.all([
        supabase.from('packaging_elements').select('*').eq('company_id', company.id).order('name'),
        supabase.from('packaging_groups').select('*').eq('company_id', company.id).order('name'),
      ]);

      if (elResult.error) {
        console.error('[PackagingContext] Error loading elements:', elResult.error.message);
      }
      if (grpResult.error) {
        console.error('[PackagingContext] Error loading groups:', grpResult.error.message);
      }

      setElements((elResult.data ?? []).map(rowToElement));
      setGroups((grpResult.data ?? []).map(rowToGroup));
    } catch (err) {
      console.error('[PackagingContext] Unexpected error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addElement = useCallback(async (el: Omit<PackagingElement, 'id'>) => {
    if (!company?.id) return;
    const { data, error } = await supabase
      .from('packaging_elements')
      .insert({
        company_id: company.id,
        name: el.name,
        unit: el.unit,
        cost_per_unit: el.costPerUnit,
        stock_qty: el.stockQty,
        reorder_level: el.reorderLevel,
        category: el.category,
        supplier: el.supplier ?? null,
        notes: el.notes ?? null,
        image_url: el.imageUrl ?? null,
      })
      .select('*')
      .single();
    if (error) {
      console.error('[PackagingContext] Error adding element:', error.message);
      return;
    }
    if (data) setElements((prev) => [...prev, rowToElement(data)]);
  }, [company?.id, supabase]);

  const updateElement = useCallback(async (id: string, el: Partial<Omit<PackagingElement, 'id'>>) => {
    if (!company?.id) return;
    const patch: Record<string, any> = {};
    if (el.name !== undefined) patch.name = el.name;
    if (el.unit !== undefined) patch.unit = el.unit;
    if (el.costPerUnit !== undefined) patch.cost_per_unit = el.costPerUnit;
    if (el.stockQty !== undefined) patch.stock_qty = el.stockQty;
    if (el.reorderLevel !== undefined) patch.reorder_level = el.reorderLevel;
    if (el.category !== undefined) patch.category = el.category;
    if (el.supplier !== undefined) patch.supplier = el.supplier ?? null;
    if (el.notes !== undefined) patch.notes = el.notes ?? null;
    if (el.imageUrl !== undefined) patch.image_url = el.imageUrl ?? null;

    const { error } = await supabase
      .from('packaging_elements')
      .update(patch)
      .eq('id', id)
      .eq('company_id', company.id);
    if (error) {
      console.error('[PackagingContext] Error updating element:', error.message);
      return;
    }
    setElements((prev) => prev.map((e) => (e.id === id ? { ...e, ...el } : e)));
  }, [company?.id, supabase]);

  const deleteElement = useCallback(async (id: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('packaging_elements')
      .delete()
      .eq('id', id)
      .eq('company_id', company.id);
    if (error) {
      console.error('[PackagingContext] Error deleting element:', error.message);
      return;
    }
    setElements((prev) => prev.filter((e) => e.id !== id));
  }, [company?.id, supabase]);

  const addGroup = useCallback(async (g: Omit<PackagingGroup, 'id'>) => {
    if (!company?.id) return;
    const { data, error } = await supabase
      .from('packaging_groups')
      .insert({
        company_id: company.id,
        name: g.name,
        description: g.description ?? null,
        items: g.items,
        image_url: g.imageUrl ?? null,
      })
      .select('*')
      .single();
    if (error) {
      console.error('[PackagingContext] Error adding group:', error.message);
      return;
    }
    if (data) setGroups((prev) => [...prev, rowToGroup(data)]);
  }, [company?.id, supabase]);

  const updateGroup = useCallback(async (id: string, g: Partial<Omit<PackagingGroup, 'id'>>) => {
    if (!company?.id) return;
    const patch: Record<string, any> = {};
    if (g.name !== undefined) patch.name = g.name;
    if (g.description !== undefined) patch.description = g.description ?? null;
    if (g.items !== undefined) patch.items = g.items;
    if (g.imageUrl !== undefined) patch.image_url = g.imageUrl ?? null;

    const { error } = await supabase
      .from('packaging_groups')
      .update(patch)
      .eq('id', id)
      .eq('company_id', company.id);
    if (error) {
      console.error('[PackagingContext] Error updating group:', error.message);
      return;
    }
    setGroups((prev) => prev.map((gr) => (gr.id === id ? { ...gr, ...g } : gr)));
  }, [company?.id, supabase]);

  const deleteGroup = useCallback(async (id: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('packaging_groups')
      .delete()
      .eq('id', id)
      .eq('company_id', company.id);
    if (error) {
      console.error('[PackagingContext] Error deleting group:', error.message);
      return;
    }
    setGroups((prev) => prev.filter((gr) => gr.id !== id));
  }, [company?.id, supabase]);

  return (
    <PackagingContext.Provider value={{ elements, groups, loading, setElements, setGroups, addElement, updateElement, deleteElement, addGroup, updateGroup, deleteGroup }}>
      {children}
    </PackagingContext.Provider>
  );
}

export function usePackaging() {
  const ctx = useContext(PackagingContext);
  if (!ctx) throw new Error('usePackaging must be used within PackagingProvider');
  return ctx;
}
